import { Component, ErrorHandler, OnDestroy, OnInit, ChangeDetectionStrategy, signal } from '@angular/core'
import { MatDialog } from '@angular/material/dialog'

import { BehaviorSubject, Observable, Subject, combineLatest, firstValueFrom, from, of } from 'rxjs'
import { map, shareReplay, switchMap, takeUntil } from 'rxjs/operators'

import { NumberDate } from '@lemon/core/model/common'
import { DateService } from '@lemon/core/service/date.service'

import { DebugService } from 'app/_angular/service/debug.service'
import { KirjanpitajaService } from 'app/_angular/service/kirjanpitaja/kirjanpitaja.service'
import { KirjautunutKayttajaService } from 'app/_angular/service/kirjautunut-kayttaja.service'
import { Kirjanpitaja, KirjanpitajanTiimi, KirjanpitajanTiimikokonaisuus, KirjanpitajanTiiminJasen } from 'app/_jaettu-lemonator/model/kirjanpitaja'

import { KirjanpitajanTiimiService } from 'app/_jaettu-lemonator/service/kirjanpitajan-tiimi.service'
import { YllapitoUriService } from 'app/_jaettu-lemonator/service/yllapito-uri.service'
import { LisaaJasenDialog, LisaaJasenDialogData } from './lisaa-jasen.dialog'
import { LisaaTiimiDialog, LisaaTiimiDialogData } from './lisaa-tiimi.dialog'
import { MuokkaaJasenyysDialog, MuokkaaJasenyysDialogData } from './muokkaa-jasenyys.dialog'
import { MuokkaaTiimiDialog, MuokkaaTiimiDialogData } from './muokkaa-tiimi.dialog'
import { ALKU_AIKOJEN_ALUSTA } from './tiimien-hallinta.service'
import { TiimiRooli } from './tiimien-hallinta.service'

interface NaytettavaJasen {
  kirjanpitajaAvain: string
  nimi: string
  alku: NumberDate
  // Null kun alku on sentinel (aikojen alusta saakka) — käytetään templaten lnumberpvm-putken kanssa jolloin tulostuu tyhjä merkkijono
  alkuNaytettava: NumberDate | null
  loppu: NumberDate | null
  primary: boolean
  aktiivinen: boolean
}

interface NaytettavaTiimi {
  avain: KirjanpitajanTiimi['avain']
  nimi: string
  jasenmaaraAktiivisia: number
  raakaTiimi: KirjanpitajanTiimi
}

interface ValittunTiimiNakyma {
  tiimi: KirjanpitajanTiimi
  esihenkilot: NaytettavaJasen[]
  jasenet: NaytettavaJasen[]
}

@Component({
  templateUrl: './tiimit.component.html',
  styleUrls: ['./tiimit.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class TiimitComponent implements OnInit, OnDestroy {
  readonly lataa = signal(true)
  readonly tiimit = signal<NaytettavaTiimi[]>([])
  readonly kokonaisuus = signal<KirjanpitajanTiimikokonaisuus | null>(null)
  readonly kokonaisuudenEsihenkilot = signal<NaytettavaJasen[]>([])

  valittunTiimiNakymaObservable: Observable<ValittunTiimiNakyma | null>

  kirjanpitajaOnDevaajaObservable: Observable<boolean>
  tiimitUriObservable: Observable<string>
  tiimitEncodedUriObservable: Observable<string>

  private _valittuTiimiAvainSubject = new BehaviorSubject<KirjanpitajanTiimi['avain'] | null>(null)
  private _paivitaSubject = new BehaviorSubject<void>(undefined)
  private _ngUnsubscribe = new Subject<void>()
  private _kirjanpitajatMap = new Map<string, Kirjanpitaja>()

  constructor(
    private _tiimiService: KirjanpitajanTiimiService,
    private _kirjanpitajaService: KirjanpitajaService,
    private _kirjautunutKayttajaService: KirjautunutKayttajaService,
    private _yllapitoUriService: YllapitoUriService,
    private _debugService: DebugService,
    private _dateService: DateService,
    private _dialog: MatDialog,
    private _errorHandler: ErrorHandler,
  ) {}

  ngOnInit() {
    const tanaan = this._dateService.currentNumberDate()

    this.kirjanpitajaOnDevaajaObservable = this._kirjautunutKayttajaService.kirjanpitajaOnDevaajaObservable
    this.tiimitUriObservable = of(this._yllapitoUriService.annaKirjanpitajienTiimitUri())
    this.tiimitEncodedUriObservable = this.tiimitUriObservable.pipe(map(uri => this._debugService.createFirestoreLink(uri)))

    const tiimitObservable = this._paivitaSubject.pipe(switchMap(() => from(this._tiimiService.annaKaikkiTiimit())))
    const kokonaisuudetObservable = this._paivitaSubject.pipe(switchMap(() => from(this._tiimiService.annaKaikkiTiimikokonaisuudet())))

    const yhdistettyObservable = combineLatest([this._kirjanpitajaService.kirjanpitajatObservable, tiimitObservable, kokonaisuudetObservable]).pipe(
      map(([kirjanpitajat, tiimit, kokonaisuudet]) => {
        const kirjanpitajatMap = new Map<string, Kirjanpitaja>()
        for (const kirjanpitaja of kirjanpitajat || []) {
          kirjanpitajatMap.set(kirjanpitaja.avain, kirjanpitaja)
        }
        return { kirjanpitajatMap, tiimit, kokonaisuudet }
      }),
      shareReplay({ bufferSize: 1, refCount: true }),
      takeUntil(this._ngUnsubscribe),
    )

    yhdistettyObservable.subscribe({
      next: ({ kirjanpitajatMap, tiimit, kokonaisuudet }) => {
        this._kirjanpitajatMap = kirjanpitajatMap
        this.tiimit.set(
          tiimit.map(tiimi => ({
            avain: tiimi.avain,
            nimi: tiimi.nimi,
            raakaTiimi: tiimi,
            jasenmaaraAktiivisia: this._laskeAktiivisetJasenet(tiimi, tanaan),
          })),
        )
        this.kokonaisuus.set(kokonaisuudet[0] ?? null)
        this.kokonaisuudenEsihenkilot.set(this.kokonaisuus() ? this._naytettavatJasenet(this.kokonaisuus().esihenkilot) : [])
        if (!this._valittuTiimiAvainSubject.value && this.tiimit().length > 0) {
          this._valittuTiimiAvainSubject.next(this.tiimit()[0].avain)
        }
        this.lataa.set(false)
      },
      error: err => {
        this._errorHandler.handleError(err)
        this.lataa.set(false)
      },
    })

    this.valittunTiimiNakymaObservable = combineLatest([this._valittuTiimiAvainSubject, yhdistettyObservable]).pipe(
      map(([avain, { tiimit }]) => {
        if (!avain) {
          return null
        }
        const tiimi = tiimit.find(t => t.avain === avain)
        if (!tiimi) {
          return null
        }
        return {
          tiimi,
          esihenkilot: this._naytettavatJasenet(tiimi.esihenkilot),
          jasenet: this._naytettavatJasenet(tiimi.jasenet),
        }
      }),
    )
  }

  ngOnDestroy() {
    this._ngUnsubscribe.next()
    this._ngUnsubscribe.complete()
  }

  valitseTiimi(tiimi: NaytettavaTiimi) {
    this._valittuTiimiAvainSubject.next(tiimi.avain)
  }

  get valittuAvain(): KirjanpitajanTiimi['avain'] | null {
    return this._valittuTiimiAvainSubject.value
  }

  naytettavaAlku(date: NumberDate | null | undefined): NumberDate | null {
    if (!date || date === ALKU_AIKOJEN_ALUSTA) {
      return null
    }
    return date
  }

  async muokkaaTiimia(tiimi: KirjanpitajanTiimi) {
    const data: MuokkaaTiimiDialogData = {
      tiimiAvain: tiimi.avain,
      nykyinenNimi: tiimi.nimi,
      nykyinenAlku: tiimi.alku,
      nykyinenLoppu: tiimi.loppu,
    }
    const ref = this._dialog.open(MuokkaaTiimiDialog, { data, width: '520px', autoFocus: false })
    const muutos = await firstValueFrom(ref.afterClosed())
    if (muutos) {
      this._paivitaSubject.next()
    }
  }

  async muokkaaTiiminJasenyytta(tiimi: KirjanpitajanTiimi, rooli: TiimiRooli, jasen: NaytettavaJasen) {
    const data: MuokkaaJasenyysDialogData = {
      nimi: jasen.nimi,
      kohde: { tyyppi: 'tiimi', tiimiAvain: tiimi.avain, rooli },
      kirjanpitajaAvain: jasen.kirjanpitajaAvain,
      jaksonAlku: jasen.alku,
      nykyinenLoppu: jasen.loppu,
      nykyinenPrimary: jasen.primary,
    }
    const ref = this._dialog.open(MuokkaaJasenyysDialog, { data, width: '480px', autoFocus: false })
    const muutos = await firstValueFrom(ref.afterClosed())
    if (muutos) {
      this._paivitaSubject.next()
    }
  }

  async muokkaaKokonaisuudenJasenyytta(jasen: NaytettavaJasen) {
    if (!this.kokonaisuus()) {
      return
    }
    const data: MuokkaaJasenyysDialogData = {
      nimi: jasen.nimi,
      kohde: { tyyppi: 'kokonaisuus', kokonaisuusAvain: this.kokonaisuus().avain },
      kirjanpitajaAvain: jasen.kirjanpitajaAvain,
      jaksonAlku: jasen.alku,
      nykyinenLoppu: jasen.loppu,
      nykyinenPrimary: jasen.primary,
    }
    const ref = this._dialog.open(MuokkaaJasenyysDialog, { data, width: '480px', autoFocus: false })
    const muutos = await firstValueFrom(ref.afterClosed())
    if (muutos) {
      this._paivitaSubject.next()
    }
  }

  async lisaaUusiTiimi() {
    const data: LisaaTiimiDialogData = {
      varatutAvaimet: this.tiimit().map(t => t.avain),
    }
    const ref = this._dialog.open(LisaaTiimiDialog, { data, width: '520px', autoFocus: false })
    const vastaus = await firstValueFrom(ref.afterClosed())
    if (vastaus?.avain) {
      this._valittuTiimiAvainSubject.next(vastaus.avain)
      this._paivitaSubject.next()
    }
  }

  async lisaaTiimiin(tiimi: KirjanpitajanTiimi, rooli: TiimiRooli) {
    const data: LisaaJasenDialogData = {
      kohde: { tyyppi: 'tiimi', tiimiAvain: tiimi.avain, tiimiNimi: tiimi.nimi, rooli },
    }
    const ref = this._dialog.open(LisaaJasenDialog, { data, width: '520px', autoFocus: false })
    const muutos = await firstValueFrom(ref.afterClosed())
    if (muutos) {
      this._paivitaSubject.next()
    }
  }

  async lisaaKokonaisuuteen() {
    if (!this.kokonaisuus()) {
      return
    }
    const data: LisaaJasenDialogData = {
      kohde: { tyyppi: 'kokonaisuus', kokonaisuusAvain: this.kokonaisuus().avain, kokonaisuusNimi: this.kokonaisuus().nimi },
    }
    const ref = this._dialog.open(LisaaJasenDialog, { data, width: '520px', autoFocus: false })
    const muutos = await firstValueFrom(ref.afterClosed())
    if (muutos) {
      this._paivitaSubject.next()
    }
  }

  private _laskeAktiivisetJasenet(tiimi: KirjanpitajanTiimi, paiva: NumberDate): number {
    let maara = 0
    for (const jasen of tiimi.esihenkilot) {
      if (this._tiimiService.onkoAktiivinenPaivamaarana(jasen, paiva)) {
        maara++
      }
    }
    for (const jasen of tiimi.jasenet) {
      if (this._tiimiService.onkoAktiivinenPaivamaarana(jasen, paiva)) {
        maara++
      }
    }
    return maara
  }

  private _naytettavatJasenet(jasenet: KirjanpitajanTiiminJasen[]): NaytettavaJasen[] {
    const tanaan = this._dateService.currentNumberDate()
    return jasenet
      .map(j => {
        const kirjanpitaja = this._kirjanpitajatMap.get(j.kirjanpitajaAvain)
        return {
          kirjanpitajaAvain: j.kirjanpitajaAvain,
          nimi: kirjanpitaja ? `${kirjanpitaja.etunimi} ${kirjanpitaja.sukunimi}` : j.kirjanpitajaAvain,
          alku: j.alku,
          alkuNaytettava: j.alku === ALKU_AIKOJEN_ALUSTA ? null : j.alku,
          loppu: j.loppu,
          primary: !!j.primary,
          aktiivinen: this._tiimiService.onkoAktiivinenPaivamaarana(j, tanaan),
        }
      })
      .sort((a, b) => {
        if (a.aktiivinen !== b.aktiivinen) {
          return a.aktiivinen ? -1 : 1
        }
        return a.nimi.localeCompare(b.nimi)
      })
  }
}
